// src/App.js - FIXED ROUTING version
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { SalonAuthProvider } from './contexts/SalonAuthContext';
import { CustomerAuthProvider } from './contexts/CustomerAuthContext';
import { SuperAdminAuthProvider } from './contexts/SuperAdminAuthContext';
import { ModuleProvider } from './contexts/ModuleContext';
import SalonProtectedRoute from './components/SalonProtectedRoute';
import CustomerProtectedRoute from './components/CustomerProtectedRoute';
import SuperAdminProtectedRoute from './components/SuperAdminProtectedRoute';
import DynamicPageTitle from './components/DynamicPageTitle';
import CustomerNavigation from './components/CustomerNavigation';

// Admin Components
import Admin from './pages/Admin';
import SalonLogin from './pages/SalonLogin';
import StaffPortal from './pages/StaffPortal';
import StaffLeavePortal from './pages/StaffLeavePortal';

// SuperAdmin Components
import SuperAdminLogin from './pages/SuperAdminLogin';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import ClientConfigurationPage from './pages/ClientConfigurationPage';

// Legal Pages Components
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';

// Customer Components - All existing components preserved
import CustomerLogin from './pages/CustomerLogin';
import VerifyCustomerEmail from './pages/VerifyCustomerEmail';
import CustomerProfileCompletion from './pages/CustomerProfileCompletion';
import CustomerDashboard from './pages/CustomerDashboard';
import ServicesBrowsing from './pages/ServicesBrowsing';
import Booking from './pages/Booking';
import ProfileManagement from './pages/ProfileManagement';
import LoyaltyProgram from './pages/LoyaltyProgram';
import Wallet from './pages/Wallet';
import CustomerPackages from './pages/CustomerPackages';
import MyAppointments from './components/MyAppointments';
import AppointmentBooking from './pages/AppointmentBooking';
import CustomerDetailsPage from './pages/CustomerDetailsPage';
import Unsubscribe from './pages/Unsubscribe';
import CustomerSignout from './pages/CustomerSignout';
import CustomerPortalAI from './pages/CustomerPortalAI';

// All CSS imports preserved
import './styles/admin.css';
import './styles/auth.css';
import './styles/customer.css';
import './styles/customerAuth.css';
import './styles/services.css';
import './styles/booking.css';
import './styles/profile.css';
import './styles/navigation.css';
import './styles/appointments.css';
import './styles/appointments-page.css';
import './styles/loyalty.css';
import './styles/customerdetails.css';
import './styles/customers.css';
import './styles/superadmin.css';
import './styles/staff-portal.css';
import './styles/admin-responsive.css';          // #Revised UI_v2 — Samsung Ultra & Oppo Pad 3 admin portal fixes
import './styles/customer-responsive.css';       // #Revised UI_v2 — Samsung Ultra & Oppo Pad 3 customer portal fixes
import './styles/ModuleManagerandCard-responsive.css'; // #Revised UI_v2 — Samsung Ultra & Oppo Pad 3 module manager fixes
import './styles/iphone17-responsive.css'; // iPhone 17 family overrides - must be last for cascade priority

// Wrapper component to include navigation on customer pages
const CustomerLayout = ({ children }) => {
  const location = useLocation();
  
  // Don't show navigation on login, verification, signout, or unsubscribe pages
  const hideNavigation = 
    location.pathname.includes('/login') || 
    location.pathname.includes('/verify-email') ||
    location.pathname.includes('/signout') ||
    location.pathname.includes('/unsubscribe-email') ||
    location.pathname.includes('/unsubscribe-push');
  
  return (
    <>
      {!hideNavigation && <CustomerNavigation />}
      {children}
    </>
  );
};

function App() {
  return (
    <Router>
      <DynamicPageTitle />
      {/* ENHANCED: Wrap entire app with all auth providers to enable simultaneous sessions */}
      <SuperAdminAuthProvider>
        <SalonAuthProvider>
          <CustomerAuthProvider>
            <ModuleProvider>
              <Routes>
                {/* SUPERADMIN ROUTES */}
                <Route path="/superadmin/login" element={<SuperAdminLogin />} />
                <Route path="/superadmin" element={<SuperAdminProtectedRoute />}>
                  <Route path="dashboard" element={<SuperAdminDashboard />} />
                  <Route path="client-config/:clientId" element={<ClientConfigurationPage />} />
                  <Route path="" element={<Navigate to="dashboard" />} />
                </Route>
                
                {/* MULTI-TENANT STRUCTURE WITH CORRECTED MODULE URLS */}
                
                {/* Admin Routes - Pattern: /BespokeBeauty/:salonId/admin/* */}
                <Route path="/BespokeBeauty/:salonId/admin/login" element={<SalonLogin />} />
                
                {/* Legal Pages - Public Access (No Authentication Required) */}
                <Route path="/BespokeBeauty/:salonId/admin/terms" element={<TermsOfService />} />
                <Route path="/BespokeBeauty/:salonId/admin/privacy" element={<PrivacyPolicy />} />
                
                {/* Admin Protected Routes */}
                <Route path="/BespokeBeauty/:salonId/admin" element={<SalonProtectedRoute />}>
                  <Route path="home" element={<Admin />} />
                  <Route path="core" element={<Admin moduleHint="core" />} />
                  <Route path="allmodules" element={<Admin moduleHint="allmodules" />} />
                  <Route path="bookings" element={<Admin moduleHint="bookings" />} />
                  <Route path="promotions" element={<Admin moduleHint="promotions" />} />
                  <Route path="communications" element={<Admin moduleHint="communications" />} />
                  <Route path="reports" element={<Admin moduleHint="reports" />} />
                  <Route path="ai" element={<Admin moduleHint="bespokeai" />} />
                  
                  {/* LEGACY REDIRECTS for backward compatibility */}
                  <Route path="book" element={<Navigate to="../bookings" replace />} />
                  <Route path="promotion" element={<Navigate to="../promotions" replace />} />
                  <Route path="campaigns" element={<Navigate to="../communications" replace />} />
                  <Route path="analytics" element={<Navigate to="../reports" replace />} />
                  
                  <Route path="customers/:customerId" element={<CustomerDetailsPage />} />
                  <Route path="" element={<Navigate to="home" />} />
                </Route>
                
                {/* Staff Portal Routes — for 'staff' role users only */}
                <Route path="/BespokeBeauty/:salonId/staff" element={<SalonProtectedRoute />}>
                  <Route path="portal" element={<StaffPortal />} />
                  <Route path="leave" element={<StaffLeavePortal />} />
                  <Route path="" element={<Navigate to="portal" />} />
                </Route>

                {/* Customer Routes - Pattern: /BespokeBeauty/:salonId/customer/* */}
                <Route path="/BespokeBeauty/:salonId/customer/login" element={<CustomerLogin />} />
                <Route path="/BespokeBeauty/:salonId/customer/verify-email" element={<VerifyCustomerEmail />} />
                
                {/* Public customer routes */}
                <Route path="/BespokeBeauty/:salonId/customer/unsubscribe-email" element={<Unsubscribe />} />
                <Route path="/BespokeBeauty/:salonId/customer/unsubscribe-push" element={<Unsubscribe />} />
                <Route path="/BespokeBeauty/:salonId/customer/signout" element={<CustomerSignout />} />
                
                {/* Customer Protected Routes with Navigation */}
                <Route path="/BespokeBeauty/:salonId/customer" element={
                  <CustomerLayout>
                    <CustomerProtectedRoute />
                  </CustomerLayout>
                }>
                  <Route path="home" element={<CustomerDashboard />} />
                  <Route path="dashboard" element={<CustomerDashboard />} />
                  <Route path="complete-profile" element={<CustomerProfileCompletion />} />
                  <Route path="my-account" element={<ProfileManagement />} />
                  <Route path="profile" element={<ProfileManagement />} />
                  <Route path="appointments" element={<MyAppointments />} />
                  <Route path="book" element={<AppointmentBooking />} />
                  <Route path="appointment-booking" element={<AppointmentBooking />} />
                  <Route path="services" element={<ServicesBrowsing />} />
                  <Route path="booking" element={<Booking />} />
                  <Route path="loyalty" element={<LoyaltyProgram />} />
                  <Route path="wallet" element={<Wallet />} />
                  <Route path="packages" element={<CustomerPackages />} />
                  <Route path="ai" element={<CustomerPortalAI />} />
                  <Route path="" element={<Navigate to="home" />} />
                </Route>
                
                {/* LEGACY ROUTES - FIXED CUSTOMER ROUTING */}
                <Route path="/login" element={<SalonLogin />} />
                <Route path="/admin" element={<Navigate to="/BespokeBeauty/demo-salon/admin/home" />} />
                
                {/* Legacy salon staff protected routes */}
                <Route element={<SalonProtectedRoute />}>
                  <Route path="/admin/customers/:salonId/:customerId" element={<CustomerDetailsPage />} />
                </Route>
                
                {/* CORRECTED Legacy customer routes - FIXED THE ISSUE */}
                <Route path="/salon/:salonId/*" element={
                  <CustomerLayout>
                    <Routes>
                      <Route path="login" element={<CustomerLogin />} />
                      <Route path="verify-email" element={<VerifyCustomerEmail />} />
                      <Route path="unsubscribe-email" element={<Unsubscribe />} />
                      <Route path="unsubscribe-push" element={<Unsubscribe />} />
                      <Route path="signout" element={<CustomerSignout />} />
                      
                      {/* FIXED: Added customer/* pattern to match /salon/:salonId/customer/* */}
                      <Route path="customer/login" element={<CustomerLogin />} />
                      <Route path="customer/verify-email" element={<VerifyCustomerEmail />} />
                      <Route path="customer/unsubscribe-email" element={<Unsubscribe />} />
                      <Route path="customer/unsubscribe-push" element={<Unsubscribe />} />
                      <Route path="customer/signout" element={<CustomerSignout />} />
                      
                      <Route element={<CustomerProtectedRoute />}>
                        <Route path="dashboard" element={<CustomerDashboard />} />
                        <Route path="complete-profile" element={<CustomerProfileCompletion />} />
                        <Route path="my-account" element={<ProfileManagement />} />
                        <Route path="profile" element={<ProfileManagement />} />
                        <Route path="appointments" element={<MyAppointments />} />
                        <Route path="book" element={<AppointmentBooking />} />
                        <Route path="appointment-booking" element={<AppointmentBooking />} />
                        <Route path="services" element={<ServicesBrowsing />} />
                        <Route path="booking" element={<Booking />} />
                        <Route path="loyalty" element={<LoyaltyProgram />} />
                        <Route path="wallet" element={<Wallet />} />

                        {/* FIXED: Added customer/* protected routes */}
                        <Route path="customer/home" element={<CustomerDashboard />} />
                        <Route path="customer/dashboard" element={<CustomerDashboard />} />
                        <Route path="customer/complete-profile" element={<CustomerProfileCompletion />} />
                        <Route path="customer/my-account" element={<ProfileManagement />} />
                        <Route path="customer/profile" element={<ProfileManagement />} />
                        <Route path="customer/appointments" element={<MyAppointments />} />
                        <Route path="customer/book" element={<AppointmentBooking />} />
                        <Route path="customer/appointment-booking" element={<AppointmentBooking />} />
                        <Route path="customer/services" element={<ServicesBrowsing />} />
                        <Route path="customer/booking" element={<Booking />} />
                        <Route path="customer/loyalty" element={<LoyaltyProgram />} />
                        <Route path="customer/wallet" element={<Wallet />} />
                        <Route path="customer/packages" element={<CustomerPackages />} />
                        <Route path="customer/ai" element={<CustomerPortalAI />} />
                        <Route path="loyalty" element={<LoyaltyProgram />} />
                        <Route path="wallet" element={<Wallet />} />
                        <Route path="packages" element={<CustomerPackages />} />
                        <Route path="ai" element={<CustomerPortalAI />} />
                      </Route>
                      
                      <Route path="" element={<Navigate to="login" replace />} />
                    </Routes>
                  </CustomerLayout>
                } />
                
                {/* FALLBACK ROUTES */}
                <Route path="/BespokeBeauty/demo-salon" element={<Navigate to="/BespokeBeauty/demo-salon/admin/login" />} />
                <Route path="/BespokeBeauty/:salonId" element={<Navigate to="/BespokeBeauty/:salonId/admin/login" />} />
                
                {/* Default redirect - SuperAdmin gets priority */}
                <Route path="/" element={<Navigate to="/superadmin/login" />} />
                
                {/* Catch-all route */}
                <Route path="*" element={<Navigate to="/superadmin/login" />} />
              </Routes>
            </ModuleProvider>
          </CustomerAuthProvider>
        </SalonAuthProvider>
      </SuperAdminAuthProvider>
    </Router>
  );
}

export default App;
